import { useAccountStore } from '@/stores/account-store'
import { dataClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import { RealtimeClient, UnauthorizedError, type RealtimeToken } from './realtime-client'

/**
 * The tab's single realtime client (Centrifugo, centrifuge-js). Created on
 * first use; the connection itself opens only while something listens (see
 * RealtimeClient). Contract:
 * docs/superpowers/specs/2026-10-21-centrifugo-realtime-design.md §5, §9.
 *
 * Auth: a Centrifugo token minted by edge-financial-api
 * (`POST /v2/realtime/token` → `{token, user, exp}`) through the shared
 * authenticated client, so 401-refresh, 432 (superseded) and the
 * identity-503 logout all apply as for any API call.
 *
 * Logout (session → null) resets the client at once; the next session
 * reconnects with a new Centrifuge instance, token, nonce and user.
 */

const DEFAULT_BASE = '/api/realtime'
const SOCKET_PATH = '/connection/websocket'

/** Statuses after which no retry can help: stop the SDK (UnauthorizedError is terminal). */
const TERMINAL_STATUSES = new Set([
  401, // survived refresh: auth-fetch already logged out
  432, // superseded by another login: auth-fetch already logged out
])

/**
 * 404: realtime not configured in this stage (no REALTIME_TOKEN_SECRET), or a
 * deploy in flight. Not terminal (that would kill realtime for the session):
 * wait this long, then fail as an ordinary error the SDK retries.
 */
export const REALTIME_NOT_CONFIGURED_RETRY_MS = 60_000

/**
 * `${base}/connection/websocket` as a ws:/wss: URL. A relative base (the
 * default, same-origin behind CloudFront and the Vite proxy, both of which
 * strip `/api/realtime`) takes the page's host and picks wss: on https pages.
 */
export function realtimeSocketUrl(
  base: string = import.meta.env.VITE_EDGE_REALTIME ?? DEFAULT_BASE,
  loc: Pick<Location, 'protocol' | 'host'> = window.location,
): string {
  const trimmed = (base || DEFAULT_BASE).replace(/\/+$/, '')
  if (/^wss?:\/\//i.test(trimmed)) return `${trimmed}${SOCKET_PATH}`
  if (/^https?:\/\//i.test(trimmed)) return `${trimmed.replace(/^http/i, 'ws')}${SOCKET_PATH}`
  const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:'
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${scheme}//${loc.host}${path}${SOCKET_PATH}`
}

/**
 * The SDK's token source (§5). No session → UnauthorizedError without a
 * request. 401 / 432 → UnauthorizedError (terminal in centrifuge-js). 404 →
 * an ordinary error after REALTIME_NOT_CONFIGURED_RETRY_MS. 403, 429, 503
 * (IDENTITY_UNRESOLVED), other 5xx and network errors are thrown as ordinary
 * errors, which the SDK retries with jittered backoff.
 * Never resolves an empty token.
 */
export async function mintRealtimeToken(): Promise<RealtimeToken> {
  if (!useAccountStore.getState().session) {
    throw new UnauthorizedError('realtime: no session')
  }
  let body: Partial<RealtimeToken> | null
  try {
    body = await dataClient.post<Partial<RealtimeToken>>('/v2/realtime/token')
  } catch (err) {
    if (err instanceof ApiError && TERMINAL_STATUSES.has(err.status)) {
      throw new UnauthorizedError(`realtime: token ${err.status}`)
    }
    if (err instanceof ApiError && err.status === 404) {
      await new Promise((resolve) => setTimeout(resolve, REALTIME_NOT_CONFIGURED_RETRY_MS))
    }
    throw err
  }
  if (!body || typeof body.token !== 'string' || !body.token || typeof body.user !== 'string') {
    throw new Error('realtime: malformed token response')
  }
  return { token: body.token, user: body.user, exp: Number(body.exp) || 0 }
}

let client: RealtimeClient | null = null
let unwatchSession: (() => void) | null = null

export function getRealtimeClient(): RealtimeClient {
  if (!client) {
    const c = new RealtimeClient({
      url: () => realtimeSocketUrl(),
      mintToken: mintRealtimeToken,
    })
    client = c
    let hadSession = !!useAccountStore.getState().session
    unwatchSession = useAccountStore.subscribe((state) => {
      const has = !!state.session
      if (has === hadSession) return
      hadSession = has
      if (has) c.resume()
      else c.reset()
    })
  }
  return client
}

/** Test-only: forget the singleton (and its session watch). */
export function __resetRealtimeClientForTests(): void {
  unwatchSession?.()
  unwatchSession = null
  client?.dispose()
  client = null
}
